import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { getThemeColors } from '../../../lib/theme';
import i18n from '../../../i18n';

interface Props {
  children: ReactNode;
  dark?: boolean;
  /** Nur für Logging/Test-Identifikation, z.B. widget.id. */
  widgetId?: string;
  /** Optionaler Text statt der Standardmeldung. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Fängt Render-Fehler eines einzelnen Dashboard-Widgets ab.
 *
 * Regression 22.09.2026 (Staging): eine einzige API-Zeile mit name:null
 * brachte Avatar zum Absturz und – mangels Boundary – das komplette
 * Admin-Dashboard auf eine weiße Seite. Mit dieser Boundary zeigt nur das
 * betroffene Widget einen Hinweis, alle anderen laufen weiter.
 *
 * Klassenkomponente, weil React-Error-Boundaries nur so möglich sind.
 */
export class WidgetErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Bewusst nur console.error: kein Telemetrie-Backend, aber im Browser
    // bleibt die Ursache nachvollziehbar.
    console.error(`[Dashboard] Widget "${this.props.widgetId ?? '?'}" konnte nicht gerendert werden:`, error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const t = getThemeColors(!!this.props.dark);
      return (
        <div role="alert" data-testid="widget-error" style={{ fontSize: 12, color: t.muted, padding: '8px 0' }}>
          {this.props.fallback ?? i18n.t('dashboard.widget.load_error')}
        </div>
      );
    }
    return this.props.children;
  }
}
